import { useNetworkStatus } from '@/hooks/useNetworkStatus'
import { useEffect, useRef } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

// Fixed toast IDs so only one of each type can exist at a time
const TOAST_ID = {
  OFFLINE: 'network-offline',
  ONLINE: 'network-online',
  WEAK: 'network-weak'
} as const

export default function NetworkStatusAlert() {
  const { t } = useTranslation()
  const { isOnline, isWeakSignal } = useNetworkStatus()
  const prevOnlineRef = useRef(isOnline)
  const prevWeakRef = useRef(isWeakSignal)
  const isMounted = useRef(false)

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true
      // On first mount: only alert if already offline
      if (!isOnline && !toast.isActive(TOAST_ID.OFFLINE)) {
        toast.error(t('No Internet Connection. Please check your network.' as any), {
          toastId: TOAST_ID.OFFLINE,
          autoClose: false // Keep visible until restored
        })
      }
      return
    }

    // Online status changed
    if (prevOnlineRef.current !== isOnline) {
      prevOnlineRef.current = isOnline

      if (!isOnline) {
        // Dismiss stale "restored" toast then show offline toast
        toast.dismiss(TOAST_ID.ONLINE)
        if (!toast.isActive(TOAST_ID.OFFLINE)) {
          toast.error(t('No Internet Connection. Please check your network.' as any), {
            toastId: TOAST_ID.OFFLINE,
            autoClose: false
          })
        }
      } else {
        // Dismiss offline toast then show restored toast briefly
        toast.dismiss(TOAST_ID.OFFLINE)
        if (!toast.isActive(TOAST_ID.ONLINE)) {
          toast.success(t('Internet Connection Restored.' as any), {
            toastId: TOAST_ID.ONLINE,
            autoClose: 3000
          })
        }
      }
    }

    // Weak signal changed
    if (prevWeakRef.current !== isWeakSignal) {
      prevWeakRef.current = isWeakSignal
      if (isWeakSignal && !toast.isActive(TOAST_ID.WEAK)) {
        toast.warning(t('Weak Network Signal. Performance may be affected.' as any), {
          toastId: TOAST_ID.WEAK,
          autoClose: 5000
        })
      } else if (!isWeakSignal) {
        toast.dismiss(TOAST_ID.WEAK)
      }
    }
  }, [isOnline, isWeakSignal, t])

  return null
}
